import { RouterView, createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: "/", redirect: "/dashboard" },

    // Standard email + password auth (Phase 1.A)
    { path: "/login", name: "login", component: () => import("@/views/Login.vue"), meta: { public: true } },
    { path: "/signup", name: "signup", component: () => import("@/views/Signup.vue"), meta: { public: true } },
    { path: "/activate", name: "activate", component: () => import("@/views/Activate.vue"), meta: { public: true } },
    {
      path: "/forgot-password",
      alias: ["/forgot"],
      name: "forgot",
      component: () => import("@/views/Forgot.vue"),
      meta: { public: true },
    },
    {
      path: "/reset-password",
      alias: ["/reset"],
      name: "reset",
      component: () => import("@/views/Reset.vue"),
      meta: { public: true },
    },

    // Private / dealer OTP flow
    { path: "/login/private", name: "private-signin", component: () => import("@/views/PrivateSignin.vue"), meta: { public: true } },
    { path: "/signup/private", name: "private-signup", component: () => import("@/views/PrivateSignup.vue"), meta: { public: true } },

    // Authenticated
    { path: "/dashboard", name: "dashboard", component: () => import("@/views/Dashboard.vue") },
    { path: "/jobcards/pending", name: "jobcards-pending", component: () => import("@/views/FleetDashboard.vue"), meta: { requiredModule: "fleet.dashboard.view" } },
    { path: "/profile", name: "profile", component: () => import("@/views/Profile.vue") },
    {
      path: "/verify-email-change",
      name: "verify-email-change",
      component: () => import("@/views/VerifyEmailChange.vue"),
    },
    { path: "/vehicles", name: "vehicles", component: () => import("@/views/Vehicles.vue") },

    // Appointments (1.C)
    { path: "/appointments", name: "appointments", component: () => import("@/views/Appointments.vue") },
    { path: "/appointments/new", name: "appointments-new", component: () => import("@/views/AppointmentNew.vue") },
    { path: "/appointments/:token", name: "appointments-detail", component: () => import("@/views/AppointmentDetail.vue") },
    { path: "/estimates", name: "estimates", component: () => import("@/views/EstimatesPage.vue"), meta: { module: "estimate.view" } },
    { path: "/invoices",  name: "invoices",  component: () => import("@/views/ClientInvoices.vue") },
    { path: "/defleet",   name: "defleet",   component: () => import("@/views/ClientDefleet.vue") },
    { path: "/jobcards", name: "client-jobcards", component: () => import("@/views/ClientJobCards.vue") },

    // Help / User manual — gated by docs.user_manual.view module.
    // Nested under a RouterView passthrough so the breadcrumb back-link
    // and any future child routes stay within the /help/manual namespace.
    { path: "/help", redirect: "/help/manual" },
    {
      path: "/help/manual",
      component: RouterView,
      meta: { requiredModule: "docs.user_manual.view" },
      children: [
        { path: "", name: "client-help", component: () => import("@/views/ClientHelp.vue") },
        { path: ":slug(.*)", name: "client-help-doc", component: () => import("@/views/ClientHelpDoc.vue") },
      ],
    },

    // ── Public customer job-status page (Report 27) — no login, bare, mobile-first ──
    { path: "/j/:token", name: "job-status", component: () => import("@/views/JobStatus.vue"), meta: { public: true, bare: true } },
    // VIR / QC standalone views are a follow-up brief — friendly placeholder for now.
    { path: "/j/v/:token", name: "job-vir", component: () => import("@/views/JobReportPending.vue"), meta: { public: true, bare: true } },
    { path: "/j/q/:token", name: "job-qc", component: () => import("@/views/JobReportPending.vue"), meta: { public: true, bare: true } },
    // Legacy SMS links: /jobStatusReport/jobCard/<encoded> → /j/<encoded>
    { path: "/jobStatusReport/jobCard/:token", redirect: (to) => ({ name: "job-status", params: { token: to.params.token } }) },

    { path: "/:catchAll(.*)", component: () => import("@/views/NotFound.vue"), meta: { public: true } },
  ],
  scrollBehavior() {
    return { top: 0 };
  },
});

const AUTH_ROUTE_NAMES = new Set([
  "login",
  "signup",
  "private-signin",
  "private-signup",
  "forgot",
]);

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (!auth.initialised) await auth.bootstrap();

  if (!to.meta.public && !auth.user) {
    return { name: "login", query: { redirect: to.fullPath } };
  }
  // If logged in and they hit any of the entry-point auth routes, bounce to dashboard.
  if (auth.user && to.name && AUTH_ROUTE_NAMES.has(String(to.name))) {
    return { name: "dashboard" };
  }
  // Module guard: redirect to dashboard if the user lacks the required module.
  const required = to.meta.requiredModule as string | undefined;
  if (required && auth.user && !auth.user.modules?.includes(required)) {
    return { name: "dashboard" };
  }
  return true;
});

import { useRequestLoader } from "@ram/ui";

router.beforeEach(() => {
  useRequestLoader().startNav();
});
router.afterEach(() => {
  useRequestLoader().endNav();
});

export { router };
